import React, { useRef, useState } from 'react';
import twemoji from 'twemoji';
import { format } from 'date-fns';
import { formatFileSize, getFileIcon } from '../../utils/config';
import { decryptMessage } from '../../utils/encryption';
import toast from 'react-hot-toast';

const REACTIONS = ['👍','❤️','😂','😮','😢','🙏','🔥','👏','😍','🎉','💯','😊'];

// Escape HTML first: the result goes into dangerouslySetInnerHTML, so raw message
// text must never be parsed as markup. twemoji only replaces emoji characters.
const escHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const twemojiHtml = (text) => twemoji.parse(escHtml(text), {
  folder: 'svg', ext: '.svg',
  base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/'
});
// WhatsApp-style inline markdown: *bold* _italic_ ~strike~ `code`
// Markers must sit on word boundaries — protects snake_case, file_names, 2*3*4, etc.
const MD_RE = /(?<![A-Za-z0-9_])(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)(?![A-Za-z0-9_])/g;
const EMOJI_RE = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}]/u;
// Plain text stays text; text with emoji goes through twemoji (formatting is applied before this).
const withEmo = (s, key) => (s && EMOJI_RE.test(s)) ? <Emo key={key} text={s}/> : s;
const renderMarkdown = (str, keyBase, EmoComp) => {
  const out = [];
  let last = 0, m, k = 0;
  MD_RE.lastIndex = 0;
  while ((m = MD_RE.exec(str)) !== null) {
    if (m.index > last) out.push(withEmo(str.slice(last, m.index), keyBase + '-t-' + (k++)));
    const tok = m[0], inner = tok.slice(1, -1);
    const style = tok[0] === '*' ? { fontWeight: 700 }
      : tok[0] === '_' ? { fontStyle: 'italic' }
      : tok[0] === '~' ? { textDecoration: 'line-through' }
      : { fontFamily: 'ui-monospace, Menlo, monospace', background: 'rgba(127,127,127,0.18)', padding: '0 3px', borderRadius: '4px' };
    out.push(<span key={keyBase + '-md-' + (k++)} style={style}>{withEmo(inner, keyBase + '-mi-' + k)}</span>);
    last = m.index + tok.length;
  }
  if (last < str.length) out.push(withEmo(str.slice(last), keyBase + '-t-' + (k++)));
  return out.length ? out : withEmo(str, keyBase + '-t');
};
export const Emo = ({ text, size = '1.1em' }) => (
  <span
    className="twemoji-wrap"
    style={{ '--twemoji-size': size }}
    dangerouslySetInnerHTML={{ __html: twemojiHtml(text) }}
  />
);

export default function MessageBubble({
  msg, isMe, showAvatar, conversationType,
  hoveredMsg, setHoveredMsg,
  msgActionMenu, setMsgActionMenu,
  setReplyTo, startEdit, deleteMessage, pinMessage, addReaction,
  setLightbox, setZoomLevel,
  chatSearchQuery, onScrollToMessage, setForwardMsg, onEnterSelect, setShowMsgInfo, onShowReactors,
  mentionNames
}) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [imgKey, setImgKey] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const scrollToMsg = (msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-teal-400', 'rounded-2xl');
      setTimeout(() => el.classList.remove('ring-2', 'ring-teal-400', 'rounded-2xl'), 2000);
    }
  };

  const copyMessage = () => {
    const text = decryptMessage(msg.content);
    if (text) { navigator.clipboard.writeText(text); toast.success('Copied!'); }
    setMsgActionMenu(null);
  };

  const decrypted = msg.message_type === 'text' ? decryptMessage(msg.content) : null;

  // Highlight search
  const renderText = (text) => {
    if (!text) return null;
    // Split URLs first, then match @mentions against real member names (longest first)
    const _names = Array.isArray(mentionNames) ? [...mentionNames].sort((a,b)=>b.length-a.length) : [];
    const urlParts = text.split(/(https?:\/\/[^\s]+)/g);
    const parts = [];
    urlParts.forEach(seg => {
      if (/^https?:\/\//.test(seg)) { parts.push(seg); return; }
      let idx = 0;
      while (idx < seg.length) {
        const at = seg.indexOf('@', idx);
        if (at === -1) { parts.push(seg.slice(idx)); break; }
        if (at > idx) parts.push(seg.slice(idx, at));
        const rest = seg.slice(at + 1);
        const hit = _names.find(n => n && rest.startsWith(n));
        if (hit) { parts.push('@' + hit); idx = at + 1 + hit.length; }
        else { parts.push('@'); idx = at + 1; }
      }
    });
    return parts.map((part, i) => {
      if (/^https?:\/\//.test(part)) {
        return (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{color: isMe ? '#ffffff' : '#2563eb', textDecoration:'underline', wordBreak:'break-all'}}>
            {part}
          </a>
        );
      }
      if (part.startsWith('@') && part.length > 1) {
        return (
          <span key={i} className="font-semibold"
            style={{color: isMe ? 'rgba(255,255,255,0.95)' : '#2563eb',
                    background: isMe ? 'rgba(255,255,255,0.2)' : 'rgba(37,99,235,0.1)',
                    borderRadius:'4px', padding:'0 3px'}}>
            {part}
          </span>
        );
      }
      if (chatSearchQuery && part.toLowerCase().includes(chatSearchQuery.toLowerCase())) {
        const escaped = chatSearchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return part.split(new RegExp(`(${escaped})`, 'gi')).map((p, j) =>
          p.toLowerCase() === chatSearchQuery.toLowerCase()
            ? <mark key={j} className="bg-yellow-300 dark:bg-yellow-600 text-gray-900 rounded px-0.5">{p}</mark>
            : p
        );
      }
      // Format first (bold/italic/strike/code), emoji are rendered inside the formatted pieces
      if (/[*_~`]/.test(part)) return <span key={i}>{renderMarkdown(part, 'k' + i)}</span>;
      if (EMOJI_RE.test(part)) return <Emo key={i} text={part}/>;
      return part;
    });
  };

  if (msg.message_type === 'system') {
    return (
      <div className="flex justify-center my-3">
        <div className="flex items-center gap-1.5 bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/50 px-4 py-1.5 rounded-full max-w-sm">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 text-teal-500 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span className="text-xs text-teal-700 dark:text-teal-300 font-medium text-center">{msg.content}</span>
        </div>
      </div>
    );
  }

  return (
    <div id={`msg-${msg.id}`}
      className={`flex mb-1 group ${isMe ? 'justify-end' : 'justify-start'}`}
      onMouseEnter={() => setHoveredMsg(msg.id)}
      onMouseLeave={() => { setHoveredMsg(null); }}>

      {/* Sender avatar */}
      {!isMe && (
        <div className="mr-2 mt-auto flex-shrink-0" style={{width:'32px'}}>
          {showAvatar && (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden">
              {msg.sender_avatar?.startsWith('https://')
                ? <img src={msg.sender_avatar?.startsWith('https://')?msg.sender_avatar:`https://qtalk.qualtechedge.in${msg.sender_avatar}`} className="w-full h-full object-cover" alt=""/>
                : (msg.sender_name?.[0] || '?').toUpperCase()}
            </div>
          )}
        </div>
      )}

      <div className={`max-w-sm min-w-0 flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>

        {/* Sender name in group */}
        {!isMe && showAvatar && conversationType === 'group' && (
          <span className="text-xs font-semibold mb-0.5 ml-1" style={{color:'#0d9488'}}>{msg.sender_name}</span>
        )}



        {/* Reply preview */}
        {msg.reply_to_message && (
          <div onClick={() => scrollToMsg(msg.reply_to_message.id)}
            className={`flex flex-col px-3 py-2 rounded-xl mb-1 border-l-4 border-teal-400 cursor-pointer hover:opacity-80 transition w-full
              ${isMe ? 'bg-teal-600/30 text-white/90' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
            <span className="text-xs font-semibold text-teal-500 dark:text-teal-400 mb-0.5">
              {msg.reply_to_message.sender_name}
            </span>
            <span className="text-xs italic flex items-center gap-1 min-w-0">
              {msg.reply_to_message.message_type === 'image' ? (
                <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px',flexShrink:0}}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>Photo</>
              ) : msg.reply_to_message.message_type === 'file' ? (
                <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px',flexShrink:0}}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg><span className="truncate">{msg.reply_to_message.file_name}</span></>
              ) : <span className="truncate">{decryptMessage(msg.reply_to_message.content)}</span>}
            </span>
          </div>
        )}

        {/* Main bubble */}
        <div className={`relative rounded-2xl text-sm shadow-sm
          ${msg.message_type === 'image' && !msg.content ? 'p-0' : msg.message_type === 'image' ? 'p-0 pb-1' : 'px-3 py-2'}
          ${isMe
            ? 'bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-tr-none'
            : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-tl-none border border-gray-100 dark:border-gray-700'}
          ${msg.is_pinned ? 'ring-2 ring-yellow-400' : ''}`}>

          {/* Forwarded */}
          {msg.is_forwarded && !msg.is_deleted && (
            <div className={`flex items-center gap-1 mb-1 text-xs italic
              ${isMe ? 'text-white/60' : 'text-gray-400'}`}>
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
                <path d="M14.59 2.59L19 7l-4.41 4.41L13 9.83V14c0 2.21-1.79 4-4 4H5v-2h4c1.1 0 2-.9 2-2V9.83l-1.59 1.58L8 10l6-6 .59.59z"/>
              </svg>
              Forwarded
            </div>
          )}

          {/* Content */}
          {msg.is_deleted ? (
            <span className="italic opacity-50 text-xs flex items-center gap-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
              This message was deleted
            </span>

          ) : msg.message_type === 'image' ? (
            <div className="relative">
              {!imgLoaded && !imgError && (
                <div className="qt-img-skeleton w-56 h-48 rounded-2xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
              )}
              {imgError && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setImgError(false); setImgLoaded(false); setImgKey(k => k + 1); }}
                  className="w-56 h-48 rounded-2xl flex flex-col items-center justify-center gap-1.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-xs font-medium">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
                  Couldn't load · Tap to retry
                </button>
              )}
              <img key={imgKey} src={msg.file_url?.startsWith("https://") ? msg.file_url : `https://qtalk.qualtechedge.in${msg.file_url}`} alt={msg.file_name}
                className={`max-w-xs rounded-2xl overflow-hidden cursor-zoom-in hover:opacity-95 transition ${imgLoaded ? 'block qt-img-fade' : 'hidden'}`}
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgError(true)}
                onClick={() => { setLightbox(msg.file_url?.startsWith('https://') ? msg.file_url : `https://qtalk.qualtechedge.in${msg.file_url}`); setZoomLevel(1); }}/>
              {/* Caption below image */}
              {msg.content && (
                <div className={`px-3 py-1.5 text-sm max-w-xs whitespace-pre-wrap break-words ${isMe ? 'text-white' : 'text-gray-800 dark:text-gray-100'}`} style={{overflowWrap:'anywhere', wordBreak:'break-word'}}>
                  {renderText(decryptMessage(msg.content))}
                </div>
              )}
              {/* Timestamp overlay on image */}
              <div className={`${msg.content ? 'relative flex justify-end px-2 pb-1' : 'absolute bottom-1 right-2'} flex items-center gap-1`}>
                <span className="text-white text-xs drop-shadow font-medium" style={{textShadow:'0 1px 3px rgba(0,0,0,0.8)'}}>
                  {format(new Date(msg.created_at), 'HH:mm')}
                </span>
                {isMe && !msg.is_deleted && (
                  msg.read_by_others ? (
                    <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                      <path d="M1 5.5L4.5 9L10 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 5.5L9.5 9L15 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                      <path d="M1 5.5L4.5 9L10 3" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 5.5L9.5 9L15 3" stroke="rgba(255,255,255,0.7)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )
                )}
              </div>
            </div>

          ) : msg.message_type === 'file' ? (
            (() => {
              const fileInfo = getFileIcon(msg.file_mime_type);
              const ext = msg.file_name?.split('.').pop()?.toUpperCase() || 'FILE';
              return (
                <div className="flex flex-col">
                <a href={`https://qtalk.qualtechedge.in/api/files/download/${msg.file_url?.split('/').pop()}`}
                  download={msg.file_name} target="_blank" rel="noreferrer"
                  className="group flex items-center gap-3 transition-all"
                  style={{minWidth:'220px', maxWidth:'300px', textDecoration:'none'}}>
                  {/* File type icon */}
                  <div className="relative flex-shrink-0" style={{width:'44px',height:'44px'}}>
                    <div className="w-full h-full rounded-2xl flex items-center justify-center"
                      style={{background: isMe ? 'rgba(255,255,255,0.18)' : fileInfo.bg}}>
                      {fileInfo.icon === 'pdf' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="12" y2="17"/>
                        </svg>
                      )}
                      {fileInfo.icon === 'doc' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>
                        </svg>
                      )}
                      {fileInfo.icon === 'sheet' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/>
                        </svg>
                      )}
                      {fileInfo.icon === 'ppt' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                        </svg>
                      )}
                      {fileInfo.icon === 'image' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                        </svg>
                      )}
                      {fileInfo.icon === 'video' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                        </svg>
                      )}
                      {fileInfo.icon === 'audio' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                        </svg>
                      )}
                      {fileInfo.icon === 'zip' && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
                        </svg>
                      )}
                      {(fileInfo.icon === 'txt' || fileInfo.icon === 'file') && (
                        <svg viewBox="0 0 24 24" fill="none" stroke={isMe ? 'rgba(255,255,255,0.9)' : fileInfo.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:'22px',height:'22px'}}>
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          <line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/>
                        </svg>
                      )}
                    </div>
                    {/* ext badge */}
                    <div className="absolute -bottom-1 -right-1 px-1 rounded-md text-white font-bold leading-none"
                      style={{fontSize:'8px', paddingTop:'2px', paddingBottom:'2px', background: isMe ? 'rgba(255,255,255,0.3)' : fileInfo.color, letterSpacing:'0.03em'}}>
                      {ext.length > 4 ? ext.slice(0,4) : ext}
                    </div>
                  </div>
                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-tight truncate" style={{color: isMe ? 'white' : 'inherit'}}>
                      {msg.file_name || 'File'}
                    </p>
                    <p className="text-xs mt-1 flex items-center gap-1.5" style={{color: isMe ? 'rgba(255,255,255,0.65)' : '#9ca3af'}}>
                      <span>{formatFileSize(msg.file_size)}</span>
                      <span style={{opacity:0.4}}>·</span>
                      <span className="flex items-center gap-1">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{width:'11px',height:'11px'}}>
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Download
                      </span>
                    </p>
                  </div>
                </a>
                {msg.content && (
                  <div className={`mt-1.5 text-sm whitespace-pre-wrap break-words ${isMe ? 'text-white' : 'text-gray-800 dark:text-gray-100'}`}
                    style={{overflowWrap:'anywhere', wordBreak:'break-word'}}>
                    {renderText(decryptMessage(msg.content))}
                  </div>
                )}
                </div>
              );
            })()

          ) : (
            <p className="whitespace-pre-wrap break-words leading-relaxed" style={{overflowWrap:'anywhere', wordBreak:'break-word'}}>
              {(() => {
                const READ_MORE_LIMIT = 350;
                const fullText = decrypted || '';
                if (fullText.length <= READ_MORE_LIMIT || expanded) {
                  return (<>
                    {renderText(fullText)}
                    {fullText.length > READ_MORE_LIMIT && (
                      <span onClick={e => { e.stopPropagation(); setExpanded(false); }}
                        style={{color: isMe ? '#a5f3fc' : '#0d9488', fontWeight:600, cursor:'pointer'}}> Show less</span>
                    )}
                  </>);
                }
                return (<>
                  {renderText(fullText.slice(0, READ_MORE_LIMIT))}
                  <span style={{opacity:0.55}}>… </span>
                  <span onClick={e => { e.stopPropagation(); setExpanded(true); }}
                    style={{color: isMe ? '#a5f3fc' : '#0d9488', fontWeight:600, cursor:'pointer'}}>Read more</span>
                </>);
              })()}
            </p>
          )}

          {/* Chevron menu button */}
          {hoveredMsg === msg.id && !msg.is_deleted && (
            <div className={`absolute -top-3 ${isMe ? '-left-8' : '-right-8'} z-20`}>
              <div className="relative">
                <button onClick={e => { e.stopPropagation(); setMsgActionMenu(msgActionMenu === msg.id ? null : msg.id); }}
                  className="w-7 h-7 rounded-full bg-white dark:bg-gray-700 hover:bg-gray-100 border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-500 transition shadow-md">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M7 10l5 5 5-5z"/></svg>
                </button>

                {msgActionMenu === msg.id && (
                  <div className={`absolute bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 py-1 z-50`}
                    style={{
                      width:'210px',
                      ...(isMe ? {right:'0'} : {left:'0'}),
                      ...((() => {
                        const el = document.getElementById(`msg-${msg.id}`);
                        if (!el) return {bottom:'8px'};
                        const rect = el.getBoundingClientRect();
                        return rect.top > 300 ? {bottom:'8px'} : {top:'8px'};
                      })())
                    }}
                    onClick={e => e.stopPropagation()}>

                    <button onClick={e => { e.stopPropagation(); setMsgActionMenu('react-'+msg.id); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 text-yellow-500"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                      React
                    </button>
                    <button onClick={() => { setReplyTo(msg); setMsgActionMenu(null); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 text-teal-500"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 00-4-4H4"/></svg>
                      Reply
                    </button>

                    {!msg.is_deleted && (
                      <button onClick={() => { onEnterSelect?.(msg.id); setMsgActionMenu(null); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 text-blue-500"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 004 4h12"/></svg>
                        Forward
                      </button>
                    )}

                    {msg.message_type === 'text' && !msg.is_deleted && (
                      <button onClick={copyMessage}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 text-gray-500"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                        Copy
                      </button>
                    )}

                    {isMe && !msg.is_deleted && msg.message_type === 'text' && (
                      <button onClick={() => startEdit(msg)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 text-purple-500"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        Edit
                      </button>
                    )}

                    <button onClick={() => { pinMessage(msg); setMsgActionMenu(null); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 text-yellow-500"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {msg.is_pinned ? 'Unpin' : 'Pin'}
                    </button>

                    {isMe && !msg.is_deleted && conversationType === 'group' && (
                      <button onClick={() => { setMsgActionMenu(null); setShowMsgInfo(msg.id); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 text-blue-500"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        Message Info
                      </button>
                    )}
                    {isMe && !msg.is_deleted && (
                      <>
                        <div className="h-px bg-gray-100 dark:bg-gray-700 mx-3 my-1"/>
                        <button onClick={() => deleteMessage(msg.id, false)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                          Delete for me
                        </button>
                        <button onClick={() => deleteMessage(msg.id, true)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                          <span className="whitespace-nowrap">Delete for everyone</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Extended emoji picker */}
        {msgActionMenu === ('react-'+msg.id) && (
          <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 p-3 mt-1 z-50 ${isMe ? 'self-end' : 'self-start'}`}
            onClick={e => e.stopPropagation()}>
            <p className="text-xs text-gray-400 text-center mb-2 font-medium">Pick a reaction</p>
            <div className="grid grid-cols-6 gap-1">
              {REACTIONS.map(e => (
                <button key={e} onClick={() => { addReaction(msg.id, e); setMsgActionMenu(null); }}
                  className="hover:scale-125 transition-transform p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Emo text={e} size="24px"/>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reactions */}
        {msg.reactions && msg.reactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isMe ? 'self-end' : 'self-start'}`}>
            {msg.reactions.map(r => (
              <button key={r.emoji} onClick={() => onShowReactors?.(msg.id)}
                className={`flex items-center gap-0.5 text-sm px-2 py-0.5 rounded-full border transition
                  ${r.reacted_by_me
                    ? 'bg-teal-50 dark:bg-teal-900/40 border-teal-400 dark:border-teal-500'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 hover:border-teal-300'}`}>
                <Emo text={r.emoji} size="17px"/>
                {(conversationType === 'group' || Number(r.count) > 1) && (
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 ml-0.5">{r.count}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Timestamp + ticks — not shown for image (overlay) */}
        {(
          <span className={`flex items-center gap-1 text-xs text-gray-400 mt-0.5 ${isMe ? 'self-end mr-1' : 'self-start ml-1'}`}>
            {msg.is_pinned && (
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 text-yellow-500">
                <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/>
              </svg>
            )}
            {format(new Date(msg.created_at), 'HH:mm')}
            {msg.edited && <span className="italic opacity-70 text-xs">edited</span>}
            {isMe && !msg.is_deleted && (
              msg.read_by_others ? (
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                  <path d="M1 5.5L4.5 9L10 3" stroke="#17C3B2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 5.5L9.5 9L15 3" stroke="#17C3B2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none">
                  <path d="M1 5.5L4.5 9L10 3" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 5.5L9.5 9L15 3" stroke="#9CA3AF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )
            )}
          </span>
        )}
      </div>
    </div>
  );
}
